import {
  createContext,
  use,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { JSX, ReactNode } from "react";
import { cx } from "./classNames";
import styles from "./ui.module.css";

/** Tone of an announcement: confirmation of a completed action, or neutral information. */
export type AnnouncementTone = "success" | "info";

/** Options of an announcement. */
export interface AnnounceOptions {
  /** Tone; defaults to `success`. */
  readonly tone?: AnnouncementTone | undefined;
}

/** Value of the announcer context. */
export interface AnnouncerContextValue {
  /**
   * Shows a short message as a toast and announces it politely to screen readers, for example
   * „Gespeichert.“ or „Antrag genehmigt.“ after an action (S-6, WCAG 4.1.3, UX-112).
   *
   * @param message - Translated message.
   * @param options - Options.
   */
  readonly announce: (message: string, options?: AnnounceOptions) => void;
}

/** A shown announcement; the sequence number makes a repeated message a new one. */
interface Announcement {
  readonly sequence: number;
  readonly message: string;
  readonly tone: AnnouncementTone;
}

/** Default time a toast stays visible, in milliseconds. */
export const defaultToastDurationMs = 6000;

const AnnouncerContext = createContext<AnnouncerContextValue | null>(null);

/** What an {@link AnnouncementOutlet} needs from the provider. */
interface OutletContextValue {
  /** The shown announcement, or `null`. */
  readonly current: Announcement | null;
  /** Registers a mounted outlet; returns the function that removes it again. */
  readonly register: () => () => void;
}

const OutletContext = createContext<OutletContextValue | null>(null);

/** Properties of {@link ToastRegion}. */
interface ToastRegionProps {
  readonly current: Announcement | null;
  /**
   * Keeps the toast while the pointer rests on it; only the bottom toast of public pages, because the
   * toast of the app shell takes no pointer events (F-61).
   */
  readonly setPaused?: ((paused: boolean) => void) | undefined;
}

/**
 * The polite live region (`role="status"`) that shows the current announcement as a toast.
 *
 * @param props - Component properties.
 * @returns The region.
 */
function ToastRegion(props: ToastRegionProps): JSX.Element {
  const { current } = props;
  const region = (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={cx(
        styles["toast"],
        current === null ? null : styles["toastVisible"],
        current?.tone === "info" ? styles["alertInfo"] : styles["alertSuccess"],
      )}
      onPointerEnter={() => {
        props.setPaused?.(true);
      }}
      onPointerLeave={() => {
        props.setPaused?.(false);
      }}
    >
      {current === null ? null : <span key={current.sequence}>{current.message}</span>}
    </div>
  );

  return region;
}

/** Properties of {@link AnnouncerProvider}. */
export interface AnnouncerProviderProps {
  /**
   * How long a toast stays visible, in milliseconds; the bottom toast of public pages stays while the
   * pointer is on it, the toast of the app shell takes no pointer events (F-61).
   */
  readonly durationMs?: number | undefined;
  /** Child elements. */
  readonly children: ReactNode;
}

/**
 * Provides {@link useAnnouncer}: one polite live region (`role="status"`) that is always in the
 * document, so screen readers announce every new message, shown as a toast. Inside the app shell
 * an {@link AnnouncementOutlet} places the toast at the top of the content area, below the app
 * bar, where it takes no pointer events, so clicks reach the controls below (F-61); without an outlet
 * (public pages) the toast sits at the bottom of the window and stays while the pointer rests on
 * it. The toast hides after `durationMs`. The state lives in the provider; there is no global state.
 *
 * @param props - Component properties.
 * @returns The provider with the live region.
 */
export function AnnouncerProvider(props: AnnouncerProviderProps): JSX.Element {
  const durationMs = props.durationMs ?? defaultToastDurationMs;
  const [current, setCurrent] = useState<Announcement | null>(null);
  const [paused, setPaused] = useState(false);
  const [outlets, setOutlets] = useState(0);
  const sequenceRef = useRef(0);

  const announce = useCallback((message: string, options?: AnnounceOptions): void => {
    sequenceRef.current += 1;
    setCurrent({ sequence: sequenceRef.current, message, tone: options?.tone ?? "success" });
  }, []);

  const register = useCallback((): (() => void) => {
    setOutlets((count) => count + 1);

    const unregister = (): void => {
      setOutlets((count) => count - 1);
    };

    return unregister;
  }, []);

  useEffect(() => {
    if (current === null || paused) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setCurrent(null);
    }, durationMs);

    return (): void => {
      window.clearTimeout(timer);
    };
  }, [current, paused, durationMs]);

  const value = useMemo<AnnouncerContextValue>(() => ({ announce }), [announce]);
  const outletValue = useMemo<OutletContextValue>(
    () => ({ current, register }),
    [current, register],
  );
  const provider = (
    <AnnouncerContext value={value}>
      <OutletContext value={outletValue}>
        {props.children}
        {outlets > 0 ? null : (
          <div className={styles["toastRegion"]}>
            <ToastRegion current={current} setPaused={setPaused} />
          </div>
        )}
      </OutletContext>
    </AnnouncerContext>
  );

  return provider;
}

/**
 * The place of the toast at the top of the content area (F-61): the first child of the main
 * landmark, sticky below the app bar and without height of its own, so nothing moves when the toast
 * appears and it never lies over the navigation or the bottom bar. On a scrolled page it may lie
 * over content for a few seconds; it takes no pointer events, so a click reaches what lies below.
 * While it is mounted the provider shows no toast of its own.
 *
 * @returns The outlet, or nothing outside an {@link AnnouncerProvider}.
 */
export function AnnouncementOutlet(): JSX.Element | null {
  const context = use(OutletContext);
  const register = context?.register;

  useLayoutEffect(() => register?.(), [register]);

  if (context === null) {
    return null;
  }

  const outlet = (
    <div className={styles["toastAnchor"]}>
      <ToastRegion current={context.current} />
    </div>
  );

  return outlet;
}

/**
 * Returns the announcer.
 *
 * @returns The context value.
 * @throws Error if no {@link AnnouncerProvider} is present.
 */
export function useAnnouncer(): AnnouncerContextValue {
  const value = use(AnnouncerContext);

  if (value === null) {
    throw new Error("useAnnouncer must be used inside an AnnouncerProvider.");
  }

  return value;
}
